import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useAuthContext } from '../../hooks/useAuthContext';
import { useTranslation } from 'react-i18next';
import '../../style/EditSettingsPage.css'; // Import the CSS file
import i18n from '../../i18n';

const EditSettingsPage = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const { dispatch } = useAuthContext();
    const [loading, setLoading] = useState(false);
    const [user, setUser] = useState({ language: 'en' });
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const storedUser = JSON.parse(localStorage.getItem('user')); // Get the user from localStorage
                const token = storedUser?.token; // Extract the token

                if (!token) {
                    throw new Error('No authentication token found');
                }

                const response = await axios.get(`${baseUrl}/api/users/account/${id}`, {
                    headers: {
                        Authorization: `Bearer ${token}`, // Include the token in the Authorization header
                    },
                });
                setUser(response.data);
            } catch (error) {
                console.log(error);
                enqueueSnackbar(t('settingsPage.fetchError'), { variant: 'error' });
            }
        };

        fetchUser();
    }, [id, enqueueSnackbar, baseUrl, t]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setUser({ ...user, [name]: value });
    };

    const handleUpdateUser = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const storedUser = JSON.parse(localStorage.getItem('user')); // Get the user from localStorage
            const token = storedUser?.token; // Extract the token

            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await axios.put(`${baseUrl}/api/users/account/${id}`, user, {
                headers: {
                    Authorization: `Bearer ${token}`, // Include the token in the Authorization header
                },
            });

            setLoading(false);
            enqueueSnackbar(t('settingsPage.updateSuccess'), { variant: 'success' });
            dispatch({ type: 'UPDATE_USER', payload: response.data });
            localStorage.setItem('user', JSON.stringify(response.data)); // Save the updated user with the token

            // Dynamically update the app's language
            i18n.changeLanguage(response.data.language);

            navigate(`/settings/${id}`);
        } catch (error) {
            setLoading(false);
            console.error('Error updating user:', error.response || error.message);
            enqueueSnackbar(t('settingsPage.updateError'), { variant: 'error' });
        }
    };

    return (
        <div className="edit-settings-page bg-dafauna-background rounded-xl shadow-md p-8">
            <h2 className="edit-settings-title text-dafauna-darkText text-2xl font-bold text-center mb-4">
                {t('settingsPage.titleEdit')}
            </h2>
            <form onSubmit={handleUpdateUser} className="edit-settings-form space-y-4">
                <div className="form-group">
                    <label htmlFor="language" className="block text-dafauna-darkText font-medium">
                        {t('settingsPage.preferredLanguage')}
                    </label>
                    <select
                        id="language"
                        name="language"
                        value={user.language}
                        onChange={handleInputChange}
                        className="form-select w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                    >
                        <option value="en">English</option>
                        <option value="es">Español</option>
                    </select>
                </div>
                <button
                    type="submit"
                    disabled={loading}
                    className="btn update-settings-btn w-full bg-dafauna-primary text-white rounded-md px-4 py-2 hover:bg-dafauna-primaryLight disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                    {loading ? t('settingsPage.updating') : t('settingsPage.updateAccount')}
                </button>
            </form>
        </div>
    );
};

export default EditSettingsPage;